// src/money.ts
function roundHalfUp(value) {
  return Math.floor(value + 0.5 + Number.EPSILON * Math.abs(value));
}
function divRound(n, d) {
  if (n < 0 || d <= 0) throw new RangeError("divRound hanya untuk n >= 0 dan d > 0");
  const num = n * 2 + d;
  if (!Number.isSafeInteger(num))
    throw new RangeError("Nilai terlalu besar untuk dihitung dengan aman");
  return Math.floor(num / (d * 2));
}
function mulDivRound(a, b, c) {
  if (a < 0 || b < 0 || c <= 0) throw new RangeError("mulDivRound hanya untuk a,b >= 0 dan c > 0");
  const [A, B, C] = [BigInt(a), BigInt(b), BigInt(c)];
  return Number((2n * A * B + C) / (2n * C));
}
function percentOfBp(amount, bp) {
  return divRound(amount * bp, 1e4);
}
function assertRupiah(value, label = "value") {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`${label} harus integer rupiah, diterima: ${value}`);
  }
}
var idr = new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 });
function formatRupiah(value) {
  const sign = value < 0 ? "-" : "";
  return `${sign}Rp ${idr.format(Math.abs(value))}`;
}

// src/calc.ts
var MAX_UNIT_PRICE = 1e9;
var MAX_QTY_MILLI = 9999999;
var MAX_SUBTOTAL = 1e11;
function toQtyMilli(qty) {
  const milli = Math.round(qty * 1e3);
  if (!(qty > 0) || Math.abs(qty * 1e3 - milli) > 1e-6 || milli > MAX_QTY_MILLI) {
    throw new RangeError("qty harus > 0, maksimal 3 desimal, dan \u2264 9.999,999");
  }
  return milli;
}
function lineGross(unitPrice, qtyMilli) {
  const g = Number((BigInt(unitPrice) * BigInt(qtyMilli) + 500n) / 1000n);
  assertRupiah(g, "gross");
  return g;
}
function roundTo(total, rule) {
  if (rule === "none") return total;
  const step = Number(rule);
  return divRound(total, step) * step;
}
function calculateOrder(inputLines, config, orderDiscount) {
  const lines = inputLines.map((l, i) => {
    assertRupiah(l.unitPrice, `lines[${i}].unitPrice`);
    if (l.unitPrice < 0 || l.unitPrice > MAX_UNIT_PRICE)
      throw new RangeError(`lines[${i}].unitPrice di luar batas`);
    const gross = lineGross(l.unitPrice, toQtyMilli(l.qty));
    const rawDiscount = l.discount ?? 0;
    assertRupiah(rawDiscount, `lines[${i}].discount`);
    if (rawDiscount < 0) throw new RangeError(`lines[${i}].discount tidak boleh negatif`);
    const discount = Math.min(rawDiscount, gross);
    return { gross, discount, net: gross - discount, taxable: l.taxable ?? true };
  });
  const subtotal = lines.reduce((s, l) => s + l.net, 0);
  if (subtotal > MAX_SUBTOTAL) throw new RangeError("Total transaksi melebihi batas");
  const lineDiscountTotal = lines.reduce((s, l) => s + l.discount, 0);
  const taxableSubtotal = lines.reduce((s, l) => s + (l.taxable ? l.net : 0), 0);
  let orderDiscountAmount = 0;
  if (orderDiscount) {
    const raw = orderDiscount.type === "amount" ? orderDiscount.value : percentOfBp(subtotal, orderDiscount.bp);
    assertRupiah(raw, "orderDiscount");
    if (raw < 0) throw new RangeError("orderDiscount tidak boleh negatif");
    orderDiscountAmount = Math.min(raw, subtotal);
  }
  const A = subtotal - orderDiscountAmount;
  const discountOnTaxable = subtotal > 0 ? mulDivRound(orderDiscountAmount, taxableSubtotal, subtotal) : 0;
  const At = taxableSubtotal - discountOnTaxable;
  const serviceInclusive = config.serviceInclusive ?? false;
  const taxOnService = config.taxOnService ?? true;
  let service = 0;
  if (config.serviceBp > 0) {
    service = serviceInclusive ? A - divRound(A * 1e4, 1e4 + config.serviceBp) : percentOfBp(A, config.serviceBp);
  }
  const serviceTaxablePart = A > 0 ? mulDivRound(service, At, A) : 0;
  let tax = 0;
  let total;
  const preTax = A + (serviceInclusive ? 0 : service);
  if (config.taxBp > 0) {
    if (config.taxInclusive) {
      const grossTaxable = serviceInclusive ? taxOnService ? At : At - serviceTaxablePart : taxOnService ? At + serviceTaxablePart : At;
      tax = grossTaxable - divRound(grossTaxable * 1e4, 1e4 + config.taxBp);
      total = preTax;
    } else {
      const base = serviceInclusive ? taxOnService ? At : At - serviceTaxablePart : taxOnService ? At + serviceTaxablePart : At;
      tax = percentOfBp(base, config.taxBp);
      total = preTax + tax;
    }
  } else {
    total = preTax;
  }
  const rounded = roundTo(total, config.rounding);
  return {
    lines,
    subtotal,
    lineDiscountTotal,
    orderDiscount: orderDiscountAmount,
    discountTotal: lineDiscountTotal + orderDiscountAmount,
    service,
    tax,
    rounding: rounded - total,
    grandTotal: rounded
  };
}
function cashChange(grandTotal, tendered) {
  assertRupiah(tendered, "tendered");
  if (tendered < grandTotal) throw new RangeError("Uang yang diterima kurang dari total");
  return tendered - grandTotal;
}
var LARGE_DISCOUNT_PERCENT = 10;
function isLargeDiscount(grossTotal, discountTotal) {
  return discountTotal * 100 > grossTotal * LARGE_DISCOUNT_PERCENT;
}

// src/schemas.ts
import { z } from "zod";
var rupiahSchema = z.number().int().safe();
var uuidSchema = z.string().uuid();
var pinLoginSchema = z.object({
  employeeId: uuidSchema,
  pin: z.string().regex(/^\d{4,6}$/, "PIN harus 4\u20136 digit")
});
var emailLoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8),
  totp: z.string().regex(/^\d{6}$/).optional()
});
var businessTypeSchema = z.enum(["fnb", "retail", "both"]);
var outletModeSchema = z.enum(["fnb", "retail"]);
var deviceModeSchema = z.enum(["pos", "kds"]);

// src/ids.ts
function uuidv7(now = Date.now(), rand = randomBytes) {
  const b = new Uint8Array(16);
  b.set(rand(16));
  b[0] = now / 2 ** 40 & 255;
  b[1] = now / 2 ** 32 & 255;
  b[2] = now / 2 ** 24 & 255;
  b[3] = now / 2 ** 16 & 255;
  b[4] = now / 2 ** 8 & 255;
  b[5] = now & 255;
  b[6] = (b[6] ?? 0) & 15 | 112;
  b[8] = (b[8] ?? 0) & 63 | 128;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
function randomBytes(n) {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
}

// src/rbac.ts
var PERMISSIONS = [
  "tenant:manage",
  "subscription:read",
  "outlets:read",
  "outlets:write",
  "employees:read",
  "employees:write",
  "roles:read",
  "roles:write",
  "devices:read",
  "devices:write",
  "catalog:read",
  "catalog:write",
  "orders:create",
  "orders:read",
  "orders:void",
  "orders:refund",
  "discounts:apply",
  "discounts:large",
  "shifts:open",
  "shifts:close",
  "cash:move",
  "inventory:read",
  "inventory:write",
  "customers:read",
  "customers:write",
  "promotions:read",
  "promotions:write",
  "reports:read",
  "settings:read",
  "settings:write",
  "audit:read",
  "kds:read",
  "kds:write"
];
var SENSITIVE_POS_PERMISSIONS = [
  "orders:void",
  "orders:refund",
  "discounts:large"
];
var SYSTEM_ROLES = {
  owner: { name: "Owner", permissions: ["*"] },
  manager: {
    name: "Manajer",
    permissions: [
      "subscription:read",
      "outlets:read",
      "employees:read",
      "employees:write",
      "roles:read",
      "devices:read",
      "devices:write",
      "catalog:read",
      "catalog:write",
      "orders:create",
      "orders:read",
      "orders:void",
      "orders:refund",
      "discounts:apply",
      "discounts:large",
      "shifts:open",
      "shifts:close",
      "cash:move",
      "inventory:read",
      "inventory:write",
      "customers:read",
      "customers:write",
      "promotions:read",
      "promotions:write",
      "reports:read",
      "settings:read",
      "audit:read",
      "kds:read",
      "kds:write"
    ]
  },
  cashier: {
    name: "Kasir",
    permissions: [
      "catalog:read",
      "orders:create",
      "orders:read",
      "discounts:apply",
      "shifts:open",
      "shifts:close",
      "cash:move",
      "customers:read",
      "customers:write",
      "kds:read"
    ]
  },
  kitchen: { name: "Dapur", permissions: ["catalog:read", "kds:read", "kds:write"] }
};
function hasPermission(granted, needed) {
  return granted.includes("*") || granted.includes(needed);
}
function hasAllPermissions(granted, needed) {
  return needed.every((p) => hasPermission(granted, p));
}
function isKnownPermission(p) {
  return PERMISSIONS.includes(p);
}

// src/pin.ts
var ITERATIONS = 12e4;
var enc = new TextEncoder();
function b64(bytes) {
  let s = "";
  bytes.forEach((b) => s += String.fromCharCode(b));
  return btoa(s);
}
function unb64(s) {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}
async function derive(pin2, salt, iterations) {
  const key = await globalThis.crypto.subtle.importKey("raw", enc.encode(pin2), "PBKDF2", false, [
    "deriveBits"
  ]);
  const bits = await globalThis.crypto.subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations },
    key,
    256
  );
  return new Uint8Array(bits);
}
function isValidPin(pin2) {
  return /^\d{4,6}$/.test(pin2);
}
async function hashPin(pin2, iterations = ITERATIONS) {
  if (!isValidPin(pin2)) throw new RangeError("PIN harus 4\u20136 digit");
  const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(pin2, salt, iterations);
  return `pbkdf2-sha256$${iterations}$${b64(salt)}$${b64(hash)}`;
}
async function verifyPin(pin2, stored) {
  const [alg, iter, salt, hash] = stored.split("$");
  if (alg !== "pbkdf2-sha256" || !iter || !salt || !hash) return false;
  const got = await derive(pin2, unb64(salt), Number(iter));
  const want = unb64(hash);
  if (got.length !== want.length) return false;
  let diff = 0;
  for (let i = 0; i < got.length; i++) diff |= (got[i] ?? 0) ^ (want[i] ?? 0);
  return diff === 0;
}

// src/auth-schemas.ts
import { z as z2 } from "zod";
var email = z2.string().trim().toLowerCase().email();
var password = z2.string().min(10, "Password minimal 10 karakter").max(128).refine((v) => /[a-z]/i.test(v) && /\d/.test(v), "Password harus mengandung huruf dan angka");
var pin = z2.string().regex(/^\d{4,6}$/, "PIN harus 4\u20136 digit");
var registerSchema = z2.object({
  businessName: z2.string().trim().min(2).max(100),
  businessType: businessTypeSchema,
  ownerName: z2.string().trim().min(2).max(100),
  email,
  password,
  outletName: z2.string().trim().min(2).max(100).optional(),
  /** Kode paket pilihan (mis. growth); kosong = paket uji coba bawaan. Harus paket yang tersedia. */
  planCode: z2.string().trim().toLowerCase().regex(/^[a-z0-9_-]{2,30}$/).optional()
});
var loginSchema = z2.object({
  email,
  password: z2.string().min(1).max(128),
  totp: z2.string().regex(/^\d{6}$/, "Kode TOTP 6 digit").optional()
});
var forgotPasswordSchema = z2.object({ email });
var resetPasswordSchema = z2.object({ token: z2.string().min(20).max(200), password });
var totpCodeSchema = z2.object({ code: z2.string().regex(/^\d{6}$/) });
var outletInputSchema = z2.object({
  name: z2.string().trim().min(2).max(100),
  code: z2.string().trim().toUpperCase().regex(/^[A-Z0-9-]{2,10}$/, "Kode 2\u201310 karakter huruf/angka").optional(),
  mode: outletModeSchema,
  address: z2.string().trim().max(300).optional(),
  phone: z2.string().trim().max(30).optional(),
  timezone: z2.enum(["Asia/Jakarta", "Asia/Makassar", "Asia/Jayapura"]).default("Asia/Jakarta")
});
var outletUpdateSchema = outletInputSchema.partial();
var permissionSchema = z2.enum(PERMISSIONS);
var roleInputSchema = z2.object({
  name: z2.string().trim().min(2).max(50),
  permissions: z2.array(permissionSchema).max(PERMISSIONS.length)
});
var roleUpdateSchema = roleInputSchema.partial();
var employeeInputSchema = z2.object({
  name: z2.string().trim().min(2).max(100),
  phone: z2.string().trim().max(30).optional(),
  roleId: uuidSchema,
  outletIds: z2.array(uuidSchema).min(1, "Pilih minimal satu outlet"),
  pin: pin.optional(),
  /** Bila diisi, pegawai juga mendapat akun Back Office. */
  email: email.optional(),
  password: password.optional()
});
var employeeUpdateSchema = z2.object({
  name: z2.string().trim().min(2).max(100).optional(),
  phone: z2.string().trim().max(30).nullable().optional(),
  roleId: uuidSchema.optional(),
  outletIds: z2.array(uuidSchema).min(1).optional(),
  active: z2.boolean().optional()
});
var resetPinSchema = z2.object({ pin });
var activationCodeInputSchema = z2.object({
  outletId: uuidSchema,
  type: deviceModeSchema
});
var activateDeviceSchema = z2.object({
  code: z2.string().trim().toUpperCase().regex(/^[A-Z2-9]{4}-?[A-Z2-9]{4}$/, "Format kode: XXXX-XXXX"),
  name: z2.string().trim().min(2).max(60)
});
var posPinLoginSchema = z2.object({ employeeId: uuidSchema, pin });
var posAuthorizeSchema = z2.object({
  /** PIN pemberi izin (Manajer). */
  employeeId: uuidSchema,
  pin,
  permission: z2.enum(["orders:void", "orders:refund", "discounts:large"]),
  /** Objek yang disetujui (mis. id transaksi); token hanya berlaku untuk objek ini. */
  ref: uuidSchema.optional()
});
var planInputSchema = z2.object({
  code: z2.string().regex(/^[a-z0-9-]{2,30}$/),
  name: z2.string().min(2).max(60),
  priceMonthly: z2.number().int().min(0),
  limits: z2.object({
    outlets: z2.number().int().min(1),
    devices: z2.number().int().min(1),
    employees: z2.number().int().min(1)
  }),
  features: z2.record(z2.boolean()).default({}),
  /** Tampil di halaman harga publik (paket khusus untuk satu klien sebaiknya tidak). */
  isPublic: z2.boolean().default(false),
  /** Lencana "Paling populer" (hanya satu paket; menandai yang baru melepas yang lama). */
  isPopular: z2.boolean().default(false)
});
var platformLoginSchema = loginSchema;
var platformChangePasswordSchema = z2.object({
  currentPassword: z2.string().min(1).max(200),
  password
});

// src/catalog-schemas.ts
import { z as z3 } from "zod";
var PRODUCT_TYPES = ["simple", "variant", "bundle", "weighed"];
var ROUNDING_RULES = ["none", "100", "500", "1000"];
var rupiah = z3.number().int("Harga harus bilangan bulat rupiah").min(0).max(1e9);
var sku = z3.string().trim().min(1).max(40).regex(/^[A-Za-z0-9._\-/]+$/, "SKU hanya boleh huruf, angka, titik, strip, garis miring");
var barcode = z3.string().trim().regex(/^[0-9A-Za-z-]{4,32}$/, "Barcode 4\u201332 karakter huruf/angka");
var nullableText = (max) => z3.string().trim().max(max).transform((v) => v === "" ? null : v).nullish();
var categoryInputSchema = z3.object({
  name: z3.string().trim().min(1).max(80),
  parentId: uuidSchema.nullish(),
  sort: z3.number().int().min(0).max(1e5).default(0),
  active: z3.boolean().default(true),
  stationId: uuidSchema.nullish()
});
var categoryUpdateSchema = categoryInputSchema.partial();
var variantInputSchema = z3.object({
  id: uuidSchema.optional(),
  name: z3.string().trim().min(1).max(60).default("Default"),
  sku: sku.nullish(),
  barcode: barcode.nullish(),
  price: rupiah,
  cost: rupiah.default(0),
  active: z3.boolean().default(true)
});
var bundleItemInputSchema = z3.object({
  variantId: uuidSchema,
  qty: z3.number().int().min(1).max(999).default(1)
});
var productBase = z3.object({
  name: z3.string().trim().min(1).max(120),
  categoryId: uuidSchema.nullish(),
  description: nullableText(500),
  type: z3.enum(PRODUCT_TYPES).default("simple"),
  unit: z3.string().trim().min(1).max(12).default("pcs"),
  taxable: z3.boolean().default(true),
  trackStock: z3.boolean().default(false),
  active: z3.boolean().default(true),
  sort: z3.number().int().min(0).max(1e5).default(0),
  stationId: uuidSchema.nullish(),
  variants: z3.array(variantInputSchema).min(1, "Minimal satu varian").max(100),
  modifierGroupIds: z3.array(uuidSchema).max(30).default([]),
  bundleItems: z3.array(bundleItemInputSchema).max(50).default([])
});
function productRules(p, ctx) {
  const names = p.variants.map((v) => v.name.toLowerCase());
  if (new Set(names).size !== names.length) {
    ctx.addIssue({ code: "custom", path: ["variants"], message: "Nama varian tidak boleh ganda" });
  }
  const skus = p.variants.map((v) => v.sku?.toLowerCase()).filter((x) => !!x);
  if (new Set(skus).size !== skus.length) {
    ctx.addIssue({ code: "custom", path: ["variants"], message: "SKU varian tidak boleh ganda" });
  }
  const barcodes = p.variants.map((v) => v.barcode).filter((x) => !!x);
  if (new Set(barcodes).size !== barcodes.length) {
    ctx.addIssue({
      code: "custom",
      path: ["variants"],
      message: "Barcode varian tidak boleh ganda"
    });
  }
  if (p.type !== "variant" && p.variants.length !== 1) {
    ctx.addIssue({
      code: "custom",
      path: ["variants"],
      message: "Produk jenis ini harus punya tepat satu varian"
    });
  }
  if (p.type === "bundle" && p.bundleItems.length === 0) {
    ctx.addIssue({
      code: "custom",
      path: ["bundleItems"],
      message: "Paket harus berisi minimal satu item"
    });
  }
  if (p.type !== "bundle" && p.bundleItems.length > 0) {
    ctx.addIssue({
      code: "custom",
      path: ["bundleItems"],
      message: "Hanya produk paket yang boleh punya isi paket"
    });
  }
  const bundleVariants = p.bundleItems.map((b) => b.variantId);
  if (new Set(bundleVariants).size !== bundleVariants.length) {
    ctx.addIssue({ code: "custom", path: ["bundleItems"], message: "Item paket ganda" });
  }
}
var productInputSchema = productBase.superRefine(productRules);
var productUpdateSchema = productBase.partial().superRefine((p, ctx) => {
  if (p.variants && p.type)
    productRules(
      { ...productBase.parse({ name: "x", variants: [{ price: 0 }] }), ...p },
      ctx
    );
});
var modifierInputSchema = z3.object({
  id: uuidSchema.optional(),
  name: z3.string().trim().min(1).max(60),
  priceDelta: z3.number().int().min(-1e6).max(1e6).default(0),
  active: z3.boolean().default(true)
});
var modifierGroupInputSchema = z3.object({
  name: z3.string().trim().min(1).max(60),
  minSelect: z3.number().int().min(0).max(20).default(0),
  maxSelect: z3.number().int().min(1).max(20).default(1),
  modifiers: z3.array(modifierInputSchema).min(1, "Minimal satu pilihan").max(50)
}).superRefine((g, ctx) => {
  if (g.minSelect > g.maxSelect) {
    ctx.addIssue({
      code: "custom",
      path: ["minSelect"],
      message: "Minimal pilihan tidak boleh melebihi maksimal"
    });
  }
  if (g.maxSelect > g.modifiers.length) {
    ctx.addIssue({
      code: "custom",
      path: ["maxSelect"],
      message: "Maksimal pilihan melebihi jumlah pilihan yang ada"
    });
  }
});
var outletProductInputSchema = z3.object({
  outletId: uuidSchema,
  variantId: uuidSchema,
  priceOverride: rupiah.nullish(),
  available: z3.boolean().default(true)
});
var outletSettingSchema = z3.object({
  taxBp: z3.number().int().min(0).max(1e4),
  taxInclusive: z3.boolean(),
  serviceBp: z3.number().int().min(0).max(1e4),
  serviceInclusive: z3.boolean(),
  taxOnService: z3.boolean(),
  rounding: z3.enum(ROUNDING_RULES),
  businessName: nullableText(100),
  npwp: nullableText(30),
  receiptHeader: nullableText(300),
  receiptFooter: nullableText(300),
  paperWidth: z3.union([z3.literal(58), z3.literal(80)]),
  blockZeroStock: z3.boolean().default(false)
});

// src/pos-schemas.ts
import { z as z4 } from "zod";
var rupiah2 = z4.number().int().min(0).max(1e11);
var ORDER_TYPES = ["take_away", "dine_in", "delivery"];
var pricingSchema = z4.object({
  taxBp: z4.number().int().min(0).max(1e4),
  taxInclusive: z4.boolean(),
  serviceBp: z4.number().int().min(0).max(1e4),
  serviceInclusive: z4.boolean(),
  taxOnService: z4.boolean(),
  rounding: z4.enum(["none", "100", "500", "1000"])
});
var posOrderItemSchema = z4.object({
  id: uuidSchema,
  variantId: uuidSchema,
  qty: z4.number().positive().max(9999.999),
  /** Harga dasar varian per unit yang tampil di kasir (tanpa modifier). */
  unitPrice: z4.number().int().min(0).max(MAX_UNIT_PRICE),
  modifiers: z4.array(
    z4.object({
      id: uuidSchema,
      priceDelta: z4.number().int().min(-1e6).max(1e6),
      /** Snapshot nama (fallback bila modifier sudah terhapus di server saat transaksi offline disinkronkan). */
      name: z4.string().max(60).optional(),
      groupName: z4.string().max(60).optional()
    })
  ).max(30).default([]),
  discount: rupiah2.default(0),
  /** Potongan promo/voucher/poin pada baris (dihitung mesin promo; server memverifikasi ulang). */
  promoDiscount: rupiah2.optional(),
  /** Snapshot status kena-pajak produk saat dijual. */
  taxable: z4.boolean(),
  /** Snapshot tampilan saat dijual (fallback bila produk sudah terhapus di server). */
  productId: uuidSchema.optional(),
  name: z4.string().max(120).optional(),
  variantName: z4.string().max(60).nullish(),
  sku: z4.string().max(40).nullish(),
  note: z4.string().trim().max(200).nullish()
}).refine((i) => i.unitPrice + i.modifiers.reduce((s, m) => s + m.priceDelta, 0) >= 0, {
  message: "Harga akhir tidak boleh negatif",
  path: ["modifiers"]
});
var offlineApprovalSchema = z4.object({
  approverId: uuidSchema,
  at: z4.string().datetime().optional(),
  /** HMAC penyetuju atas (transaksi, penyetuju, waktu); lihat sync-proof.ts. */
  proof: z4.string().max(200).optional()
});
var orderDiscountInputSchema = z4.object({
  type: z4.enum(["amount", "percent"]),
  /** rupiah (amount) atau basis poin (percent; 1000 = 10%) */
  value: z4.number().int().min(0).max(1e11),
  reason: z4.string().trim().max(100).nullish()
}).refine((d) => d.type === "amount" || d.value <= 1e4, {
  message: "Diskon persen maksimal 100%",
  path: ["value"]
});
var cashPaymentSchema = z4.object({
  id: uuidSchema,
  method: z4.literal("cash"),
  amount: rupiah2,
  tendered: rupiah2,
  change: rupiah2
});
var NON_CASH_METHODS = [
  "qris",
  "gopay",
  "va_bca",
  "va_bni",
  "va_bri",
  "va_permata"
];
var PAYMENT_LABELS = {
  cash: "Tunai",
  qris: "QRIS",
  gopay: "GoPay",
  va_bca: "VA BCA",
  va_bni: "VA BNI",
  va_bri: "VA BRI",
  va_permata: "VA Permata"
};
var nonCashPaymentSchema = z4.object({
  id: uuidSchema,
  method: z4.enum(NON_CASH_METHODS),
  amount: z4.number().int().min(1).max(1e11)
});
var orderTotalsSchema = z4.object({
  subtotal: rupiah2,
  discountTotal: rupiah2,
  service: rupiah2,
  tax: rupiah2,
  rounding: z4.number().int().min(-1e3).max(1e3),
  grandTotal: rupiah2
});
var orderNoSchema = z4.string().regex(/^[A-Z0-9]{2,10}(-[A-Z0-9]{1,10}){2,4}$/, "Nomor transaksi tidak valid");
var posOrderSchema = z4.object({
  id: uuidSchema,
  shiftId: uuidSchema,
  orderNo: orderNoSchema,
  type: z4.enum(ORDER_TYPES).default("take_away"),
  clientCreatedAt: z4.string().datetime(),
  items: z4.array(posOrderItemSchema).min(1, "Keranjang kosong").max(100),
  discount: orderDiscountInputSchema.nullish(),
  /** Token persetujuan Manajer (dari /pos/auth/authorize) untuk diskon besar. */
  authorizationToken: z4.string().min(20).max(2e3).nullish(),
  offlineApproval: offlineApprovalSchema.nullish(),
  note: z4.string().trim().max(200).nullish(),
  /** Pelanggan (online saja), kode voucher, dan poin yang ditukar (Fase 8). */
  customerId: uuidSchema.nullish(),
  voucherCode: z4.string().trim().toUpperCase().max(40).nullish(),
  pointsRedeemed: z4.number().int().min(0).max(1e8).optional(),
  /** F&B: bill terbuka asal transaksi ini + snapshot meja. */
  billId: uuidSchema.nullish(),
  tableId: uuidSchema.nullish(),
  tableName: z4.string().trim().max(40).nullish(),
  guests: z4.number().int().min(1).max(99).nullish(),
  pricing: pricingSchema,
  totals: orderTotalsSchema,
  /** 1 tunai, atau 1 non-tunai (+ opsional 1 tunai = split). Non-tunai hanya lewat alur intent pembayaran. */
  payments: z4.array(z4.union([cashPaymentSchema, nonCashPaymentSchema])).min(1).max(2).refine(
    (p) => p.filter((x) => x.method === "cash").length <= 1,
    "Maksimal satu pembayaran tunai"
  ).refine(
    (p) => p.filter((x) => x.method !== "cash").length <= 1,
    "Maksimal satu pembayaran non-tunai"
  )
});
var shiftOpenSchema = z4.object({
  id: uuidSchema,
  openingCash: rupiah2,
  clientCreatedAt: z4.string().datetime().optional()
});
var shiftCloseSchema = z4.object({
  countedCash: rupiah2,
  note: z4.string().trim().max(200).nullish(),
  /** Waktu penutupan menurut jam perangkat (penting untuk penutupan offline). */
  closedAt: z4.string().datetime().optional()
});
var cashMovementSchema = z4.object({
  id: uuidSchema,
  shiftId: uuidSchema,
  type: z4.enum(["in", "out"]),
  amount: z4.number().int().min(1).max(1e11),
  note: z4.string().trim().min(2).max(100),
  clientCreatedAt: z4.string().datetime().optional()
});
var voidOrderSchema = z4.object({
  reason: z4.string().trim().min(3).max(200),
  authorizationToken: z4.string().min(20).max(2e3).nullish(),
  offlineApproval: offlineApprovalSchema.nullish()
});
var itemUnitPrice = (i) => i.unitPrice + i.modifiers.reduce((s, m) => s + m.priceDelta, 0);
function orderCalcLines(items) {
  return items.map((i) => ({
    unitPrice: itemUnitPrice(i),
    qty: i.qty,
    discount: i.discount + (i.promoDiscount ?? 0),
    taxable: i.taxable
  }));
}
function toOrderDiscount(d) {
  if (!d) return void 0;
  return d.type === "amount" ? { type: "amount", value: d.value } : { type: "percent", bp: d.value };
}
function computeOrder(items, pricing, discount) {
  const cfg = pricing;
  return calculateOrder(orderCalcLines(items), cfg, toOrderDiscount(discount));
}
function totalsOf(r) {
  return {
    subtotal: r.subtotal,
    discountTotal: r.discountTotal,
    service: r.service,
    tax: r.tax,
    rounding: r.rounding,
    grandTotal: r.grandTotal
  };
}
function buildOrderNo(outletCode, deviceId, seq, now = /* @__PURE__ */ new Date(), tzOffsetMinutes = 7 * 60) {
  const local = new Date(now.getTime() + tzOffsetMinutes * 6e4);
  const yy = String(local.getUTCFullYear()).slice(2);
  const mm = String(local.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(local.getUTCDate()).padStart(2, "0");
  const dev = deviceId.replace(/-/g, "").slice(-6).toUpperCase();
  const code = outletCode.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10) || "POS";
  return `${code}-${yy}${mm}${dd}-${dev}-${String(seq).padStart(4, "0")}`;
}
var SYNC_TYPES = [
  "shift.open",
  "shift.close",
  "order.create",
  "order.void",
  "cash.movement"
];
var MAX_SYNC_BATCH = 50;
var syncEnvelopeSchema = z4.object({
  items: z4.array(
    z4.object({
      /** id outbox (UUIDv7) */
      id: uuidSchema,
      seq: z4.number().int().min(0),
      type: z4.enum(SYNC_TYPES),
      /** pegawai yang melakukan aksi di perangkat */
      employeeId: uuidSchema,
      createdAt: z4.string().datetime(),
      payload: z4.unknown(),
      /** HMAC pegawai atas item ini (lihat sync-proof.ts); wajib bila server SYNC_PROOF=enforce. */
      proof: z4.string().max(200).optional()
    })
  ).min(1).max(MAX_SYNC_BATCH)
});
var shiftClosePayloadSchema = shiftCloseSchema.extend({ shiftId: uuidSchema });
var orderVoidPayloadSchema = voidOrderSchema.extend({
  orderId: uuidSchema,
  voidedAt: z4.string().datetime().optional()
});
var syncPayloadSchemas = {
  "shift.open": shiftOpenSchema,
  "shift.close": shiftClosePayloadSchema,
  "order.create": posOrderSchema,
  "order.void": orderVoidPayloadSchema,
  "cash.movement": cashMovementSchema
};
var ORDER_FLAGS = {
  price_mismatch: "Harga berbeda dari harga server saat disinkronkan",
  settings_mismatch: "Pengaturan pajak/service berbeda dari server",
  calc_mismatch: "Total yang dikirim kasir berbeda dari hitungan server",
  clock_skew: "Jam perangkat tidak cocok dengan server (> 24 jam)",
  item_missing: "Produk/varian sudah tidak ada di server",
  offline_approval: "Persetujuan Manajer diberikan offline (PIN diverifikasi di perangkat)",
  discount_unapproved: "Diskon besar tanpa persetujuan yang sah",
  employee_inactive: "Pegawai sudah nonaktif saat disinkronkan",
  permission_missing: "Pegawai tidak (lagi) berwenang membuat transaksi",
  late_sync: "Disinkronkan setelah shift ditutup di server",
  bill_closed: "Bill sudah ditutup/dibayar dari perangkat lain saat disinkronkan",
  promo_mismatch: "Potongan promo/poin dari kasir berbeda dari hitungan server",
  voucher_issue: "Voucher tidak sah / kuota habis saat disinkronkan",
  points_issue: "Penukaran poin tidak sah / saldo kurang saat disinkronkan",
  customer_missing: "Pelanggan tidak ditemukan saat disinkronkan",
  approval_proof_invalid: "Persetujuan Manajer offline tanpa bukti tanda tangan yang sah (penyetuju tidak terverifikasi)",
  order_no_renamed: "Nomor transaksi kembar dengan perangkat lain; diberi akhiran saat disinkronkan"
};
function compareVersions(a, b) {
  const pa = a.split(".").map((x) => parseInt(x, 10) || 0);
  const pb = b.split(".").map((x) => parseInt(x, 10) || 0);
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

// src/fnb-schemas.ts
import { z as z5 } from "zod";
var name = (max) => z5.string().trim().min(1).max(max);
var sort = z5.number().int().min(0).max(1e5).default(0);
var stationInputSchema = z5.object({ name: name(40), sort });
var areaInputSchema = z5.object({ name: name(40), sort });
var tableInputSchema = z5.object({
  name: name(20),
  areaId: uuidSchema.nullish(),
  capacity: z5.number().int().min(1).max(99).default(4),
  sort
});
var tableBulkSchema = z5.object({
  areaId: uuidSchema.nullish(),
  prefix: z5.string().trim().max(15).default("Meja"),
  from: z5.number().int().min(1).max(999).default(1),
  count: z5.number().int().min(1).max(100),
  capacity: z5.number().int().min(1).max(99).default(4)
});
var BILL_TYPES = ORDER_TYPES;
var billUpsertSchema = z5.object({
  tableId: uuidSchema.nullish(),
  name: z5.string().trim().max(40).nullish(),
  type: z5.enum(BILL_TYPES).default("dine_in"),
  guests: z5.number().int().min(1).max(99).default(1),
  items: z5.array(posOrderItemSchema).max(100).default([]),
  /** Versi bill yang dilihat perangkat; beda dengan server -> 409 BILL_CHANGED. Kosong saat bill baru. */
  expectedVersion: z5.number().int().min(1).optional()
});
var billSendSchema = billUpsertSchema.extend({ sendId: uuidSchema });
var approval = {
  authorizationToken: z5.string().min(20).max(2e3).nullish()
};
var billVoidItemSchema = z5.object({
  reason: z5.string().trim().min(3).max(200),
  ...approval
});
var billCancelSchema = z5.object({
  reason: z5.string().trim().min(3).max(200),
  ...approval
});
var billMoveSchema = z5.object({ tableId: uuidSchema.nullable() });
var billMergeSchema = z5.object({ sourceId: uuidSchema });
var billSplitSchema = z5.object({
  newBillId: uuidSchema,
  itemIds: z5.array(uuidSchema).min(1).max(100),
  tableId: uuidSchema.nullish(),
  name: z5.string().trim().max(40).nullish()
});
var billPayRequestSchema = z5.object({ requested: z5.boolean().default(true) });
var TICKET_STATUSES = ["new", "preparing", "ready", "served", "cancelled"];
var ticketPatchSchema = z5.object({ status: z5.enum(["preparing", "ready", "served"]) });
var ticketItemPatchSchema = z5.object({ done: z5.boolean() });
var soldOutSchema = z5.object({ variantId: uuidSchema, available: z5.boolean() });
function canTransitionTicket(from, to) {
  if (from === "cancelled") return false;
  const rank = { new: 0, preparing: 1, ready: 2, served: 3 };
  if (from === "ready" && to === "preparing") return true;
  return rank[to] > rank[from];
}

// src/payment-schemas.ts
import { z as z6 } from "zod";
var PAYMENT_ENVIRONMENTS = ["simulator", "sandbox", "production"];
var PAYMENT_TTL_MINUTES = 15;
var paymentConfigSchema = z6.object({
  environment: z6.enum(PAYMENT_ENVIRONMENTS),
  merchantCode: z6.string().trim().max(40).nullish(),
  clientKey: z6.string().trim().max(100).nullish(),
  /** Kosong/tidak dikirim = pertahankan yang tersimpan. */
  serverKey: z6.string().trim().min(10).max(200).optional(),
  enabledMethods: z6.array(z6.enum(NON_CASH_METHODS)).max(NON_CASH_METHODS.length)
});
var paymentIntentCreateSchema = z6.object({
  id: uuidSchema,
  order: posOrderSchema
});
var PAYMENT_STATUSES = ["pending", "success", "failed", "expired", "cancelled"];
function mapMidtransStatus(transactionStatus, fraudStatus) {
  switch (transactionStatus) {
    case "settlement":
      return "success";
    case "capture":
      return !fraudStatus || fraudStatus === "accept" ? "success" : "pending";
    case "pending":
      return "pending";
    case "expire":
      return "expired";
    case "cancel":
      return "cancelled";
    case "deny":
    case "failure":
      return "failed";
    default:
      return "pending";
  }
}

// src/inventory-schemas.ts
import { z as z7 } from "zod";
var qty3 = z7.number().max(1e6).refine((n) => Math.abs(n * 1e3 - Math.round(n * 1e3)) < 1e-6, "Maksimal 3 desimal");
var qtyPos = qty3.refine((n) => n > 0, "Harus lebih dari 0");
var rupiah3 = z7.number().int().min(0).max(1e11);
var text = (max) => z7.string().trim().max(max);
var STOCK_MOVEMENT_TYPES = [
  "sale",
  "recipe_use",
  "refund",
  "receive",
  "adjust",
  "transfer_out",
  "transfer_in",
  "opname",
  "repack_out",
  "repack_in"
];
var MOVEMENT_LABELS = {
  sale: "Penjualan",
  recipe_use: "Pemakaian resep",
  refund: "Retur / batal",
  receive: "Penerimaan",
  adjust: "Penyesuaian",
  transfer_out: "Transfer keluar",
  transfer_in: "Transfer masuk",
  opname: "Opname",
  repack_out: "Pecah stok (keluar)",
  repack_in: "Pecah stok (hasil)"
};
var stockAdjustSchema = z7.object({
  outletId: uuidSchema,
  variantId: uuidSchema,
  /** negatif = berkurang (rusak/hilang), positif = bertambah */
  qty: qty3.refine((n) => n !== 0, "Tidak boleh 0"),
  reason: text(200).min(3)
});
var repackLine = z7.object({
  variantId: uuidSchema,
  qty: qtyPos,
  /** isi per unit dalam satuan produk (mis. 25 untuk karung 25 kg); diingat untuk pecah stok berikutnya */
  content: qtyPos.optional()
});
var stockRepackSchema = z7.object({
  /** dibuat klien (UUID) agar pengiriman ulang tidak menggandakan */
  id: uuidSchema,
  outletId: uuidSchema,
  source: repackLine,
  outputs: z7.array(repackLine).min(1, "Minimal satu barang hasil").max(20),
  note: text(200).nullish()
}).refine(
  (v) => (/* @__PURE__ */ new Set([v.source.variantId, ...v.outputs.map((o) => o.variantId)])).size === v.outputs.length + 1,
  { message: "Barang sumber dan hasil tidak boleh sama atau ganda", path: ["outputs"] }
);
var stockMinSchema = z7.object({
  outletId: uuidSchema,
  variantId: uuidSchema,
  minQty: qty3.refine((n) => n >= 0, "Tidak boleh negatif")
});
var supplierInputSchema = z7.object({
  name: text(100).min(1),
  contact: text(100).nullish(),
  phone: text(30).nullish(),
  terms: text(100).nullish(),
  note: text(300).nullish()
});
var poCreateSchema = z7.object({
  outletId: uuidSchema,
  supplierId: uuidSchema,
  note: text(300).nullish(),
  expectedAt: z7.string().datetime().nullish(),
  items: z7.array(z7.object({ variantId: uuidSchema, qty: qtyPos, unitCost: rupiah3 })).min(1, "Minimal satu barang").max(200)
});
var poUpdateSchema = poCreateSchema.omit({ outletId: true }).partial();
var poReceiveSchema = z7.object({
  items: z7.array(z7.object({ itemId: uuidSchema, qty: qtyPos, unitCost: rupiah3.optional() })).min(1).max(200)
});
var opnameCreateSchema = z7.object({
  outletId: uuidSchema,
  note: text(300).nullish(),
  /** kosong = seluruh barang terlacak stok */
  variantIds: z7.array(uuidSchema).max(2e3).optional()
});
var opnameCountSchema = z7.object({
  items: z7.array(
    z7.object({
      variantId: uuidSchema,
      counted: qty3.refine((n) => n >= 0, "Tidak boleh negatif")
    })
  ).min(1).max(2e3)
});
var transferCreateSchema = z7.object({
  fromOutletId: uuidSchema,
  toOutletId: uuidSchema,
  note: text(300).nullish(),
  items: z7.array(z7.object({ variantId: uuidSchema, qty: qtyPos })).min(1).max(200)
}).refine((t) => t.fromOutletId !== t.toOutletId, {
  message: "Outlet asal & tujuan harus berbeda",
  path: ["toOutletId"]
});
var transferReceiveSchema = z7.object({
  /** kosong = diterima sesuai jumlah dikirim */
  items: z7.array(z7.object({ variantId: uuidSchema, qty: qty3.refine((n) => n >= 0) })).max(200).optional()
});
var recipeSchema = z7.object({
  items: z7.array(z7.object({ componentVariantId: uuidSchema, qty: qtyPos })).max(50).refine((a) => new Set(a.map((x) => x.componentVariantId)).size === a.length, "Bahan ganda")
});
var returnCreateSchema = z7.object({
  id: uuidSchema,
  orderId: uuidSchema,
  reason: text(200).min(3),
  /** kembalikan barang ke stok (default ya) */
  restock: z7.boolean().default(true),
  items: z7.array(z7.object({ orderItemId: uuidSchema, qty: qtyPos })).min(1).max(100),
  authorizationToken: z7.string().min(20).max(2e3).nullish()
});
function movingAverageCost(oldQty, oldAvg, inQty, inCost) {
  if (inQty <= 0) return oldAvg;
  if (oldQty <= 0) return inCost;
  return Math.round((oldQty * oldAvg + inQty * inCost) / (oldQty + inQty));
}
function computeReturnRefund(order, lines, alreadyRefunded = 0) {
  const base = order.items.reduce((s, i) => s + i.lineNet, 0);
  const amounts = lines.map((l) => {
    const it = order.items.find((i) => i.id === l.orderItemId);
    if (!it || base <= 0 || it.qtyMilli <= 0) return { orderItemId: l.orderItemId, amount: 0 };
    const net = it.lineNet * l.qtyMilli / it.qtyMilli;
    return { orderItemId: l.orderItemId, amount: Math.round(order.grandTotal * net / base) };
  });
  let total = amounts.reduce((s, a) => s + a.amount, 0);
  const room = Math.max(0, order.grandTotal - alreadyRefunded);
  if (total > room && amounts.length > 0) {
    const cut = total - room;
    const last = amounts[amounts.length - 1];
    last.amount = Math.max(0, last.amount - cut);
    total = amounts.reduce((s, a) => s + a.amount, 0);
  }
  return { amounts, total };
}

// src/time.ts
function localParts(at, timezone) {
  let tz = timezone;
  try {
    new Intl.DateTimeFormat("en-CA", { timeZone: tz });
  } catch {
    tz = "Asia/Jakarta";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23"
  }).formatToParts(at);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "00";
  return { date: `${get("year")}-${get("month")}-${get("day")}`, hour: Number(get("hour")) % 24 };
}

// src/platform-schemas.ts
import { z as z8 } from "zod";
var FEATURES = {
  // F&B
  tables: "Meja & open bill (denah, split/pindah/gabung bill)",
  kds: "Layar dapur (KDS) & stasiun",
  // Stok
  inventory: "Inventori (stok, opname, transfer antar-outlet)",
  purchasing: "Pembelian & pemasok (PO, penerimaan barang)",
  recipes: "Resep (BOM) & potong stok bahan",
  returns: "Retur penjualan",
  repack: "Pecah stok (karung \u2192 kemasan kecil)",
  // Pelanggan & promosi
  crm: "Data pelanggan",
  loyalty: "Program poin (loyalitas)",
  promos: "Promo otomatis",
  vouchers: "Voucher",
  // Pembayaran & laporan
  payments_noncash: "Pembayaran non-tunai (Midtrans)",
  reports_export: "Ekspor laporan (CSV/XLSX/PDF)",
  // Katalog & operasional
  catalog_import: "Impor/ekspor katalog (CSV)",
  thermal_print: "Cetak struk thermal (Bluetooth/USB)",
  multi_outlet: "Multi-outlet (lebih dari satu outlet)",
  // Keamanan
  audit_log: "Log aktivitas",
  totp: "Verifikasi dua langkah (TOTP)"
};
var FEATURE_KEYS = Object.keys(FEATURES);
var isFeatureKey = (k) => k in FEATURES;
var FEATURE_GROUPS = [
  { title: "F&B", keys: ["tables", "kds"] },
  { title: "Stok", keys: ["inventory", "purchasing", "recipes", "returns", "repack"] },
  { title: "Pelanggan & promosi", keys: ["crm", "loyalty", "promos", "vouchers"] },
  { title: "Pembayaran & laporan", keys: ["payments_noncash", "reports_export"] },
  { title: "Katalog & operasional", keys: ["catalog_import", "thermal_print", "multi_outlet"] },
  { title: "Keamanan", keys: ["audit_log", "totp"] }
];
var FEATURE_PARENT = {
  tables: "fnb",
  kds: "fnb",
  purchasing: "inventory",
  recipes: "inventory",
  returns: "inventory",
  repack: "inventory"
};
var LEGACY_FEATURE_KEYS = ["fnb"];
function resolveFeatures(planFeatures, overrides = {}) {
  const plan = planFeatures ?? {};
  const o = overrides instanceof Map ? overrides : new Map(Object.entries(overrides));
  const out = {};
  for (const k of FEATURE_KEYS) {
    const parent = FEATURE_PARENT[k];
    out[k] = o.get(k) ?? (parent ? o.get(parent) : void 0) ?? plan[k] ?? (parent ? plan[parent] : void 0) ?? true;
  }
  return out;
}
var ANNOUNCEMENT_LEVELS = ["info", "warning", "critical"];
var announcementInputSchema = z8.object({
  title: z8.string().trim().min(3).max(100),
  body: z8.string().trim().min(3).max(600),
  level: z8.enum(ANNOUNCEMENT_LEVELS).default("info"),
  startsAt: z8.string().datetime().nullish(),
  endsAt: z8.string().datetime().nullish(),
  active: z8.boolean().default(true)
}).refine((a) => !a.startsAt || !a.endsAt || a.startsAt < a.endsAt, {
  message: "Waktu selesai harus setelah waktu mulai",
  path: ["endsAt"]
});
var tenantFeatureSchema = z8.object({
  key: z8.string().refine(
    (k) => isFeatureKey(k) || LEGACY_FEATURE_KEYS.includes(k),
    "Fitur tidak dikenal"
  ),
  enabled: z8.boolean().nullable()
});
var impersonateSchema = z8.object({
  reason: z8.string().trim().min(10, "Alasan minimal 10 karakter").max(300)
});
var IMPERSONATION_TTL_SEC = 1800;
var tenantPlanSchema = z8.object({ planId: uuidSchema });

// src/promo-engine.ts
import { z as z9 } from "zod";
var PROMO_KINDS = ["percent", "amount", "bogo"];
var PROMO_SCOPES = ["order", "product", "category"];
var hhmm = z9.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Format jam HH:MM");
var rupiahInt = z9.number().int().min(0).max(1e11);
var promoBodySchema = z9.object({
  name: z9.string().trim().min(2).max(80),
  kind: z9.enum(PROMO_KINDS),
  scope: z9.enum(PROMO_SCOPES),
  /** product/category id sasaran (kosong bila scope = order). */
  targetIds: z9.array(uuidSchema).max(200).default([]),
  /** percent: basis poin (1000 = 10%). */
  valueBp: z9.number().int().min(0).max(1e4).default(0),
  /** amount: potongan rupiah (per transaksi bila scope=order, per unit barang bila product/category). */
  valueAmount: rupiahInt.default(0),
  /** bogo: beli `buyQty` gratis `getQty` (unit utuh, barang yang sama). */
  buyQty: z9.number().int().min(1).max(99).default(1),
  getQty: z9.number().int().min(1).max(99).default(1),
  /** Minimum belanja (jumlah barang yang memenuhi syarat, setelah diskon sebelumnya). */
  minSpend: rupiahInt.default(0),
  /** Batas potongan maksimum per transaksi (0 = tanpa batas). */
  maxDiscount: rupiahInt.default(0),
  /** 0 = Minggu … 6 = Sabtu; kosong = setiap hari. */
  days: z9.array(z9.number().int().min(0).max(6)).max(7).default([]),
  timeFrom: hhmm.nullish(),
  timeTo: hhmm.nullish(),
  startsAt: z9.string().datetime().nullish(),
  endsAt: z9.string().datetime().nullish(),
  /** Kosong = semua outlet. */
  outletIds: z9.array(uuidSchema).max(100).default([]),
  /** false = eksklusif: tidak ditumpuk dengan promo lain pada baris yang sama. */
  stackable: z9.boolean().default(false),
  /** Lebih besar = dievaluasi lebih dulu. */
  priority: z9.number().int().min(0).max(1e3).default(100),
  /** true = hanya aktif bila kode voucher terkait dimasukkan. */
  requiresVoucher: z9.boolean().default(false),
  active: z9.boolean().default(true)
}).superRefine((p, ctx) => {
  if (p.kind === "percent" && (p.valueBp < 1 || p.valueBp > 1e4))
    ctx.addIssue({ code: "custom", path: ["valueBp"], message: "Persen 0,01%\u2013100%" });
  if (p.kind === "amount" && p.valueAmount < 1)
    ctx.addIssue({ code: "custom", path: ["valueAmount"], message: "Nominal minimal Rp 1" });
  if (p.kind === "bogo" && p.scope === "order")
    ctx.addIssue({
      code: "custom",
      path: ["scope"],
      message: "Beli X gratis Y butuh sasaran barang"
    });
  if (p.scope !== "order" && p.targetIds.length === 0)
    ctx.addIssue({ code: "custom", path: ["targetIds"], message: "Pilih minimal satu sasaran" });
  if (p.timeFrom == null !== (p.timeTo == null))
    ctx.addIssue({ code: "custom", path: ["timeTo"], message: "Isi jam mulai dan jam selesai" });
  if (p.startsAt && p.endsAt && p.startsAt > p.endsAt)
    ctx.addIssue({
      code: "custom",
      path: ["endsAt"],
      message: "Tanggal akhir sebelum tanggal awal"
    });
});
var toMinutes = (s) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
function localClock(at, timezone) {
  let tz = timezone;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
  } catch {
    tz = "Asia/Jakarta";
  }
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(at);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "";
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return {
    dow: dow < 0 ? 0 : dow,
    minutes: Number(get("hour")) % 24 * 60 + Number(get("minute"))
  };
}
function promoActiveFor(p, ctx) {
  if (!p.active) return false;
  if (p.requiresVoucher && !ctx.voucherPromoIds?.has(p.id)) return false;
  if (p.outletIds.length > 0 && !p.outletIds.includes(ctx.outletId)) return false;
  const t = ctx.at.getTime();
  if (p.startsAt && t < Date.parse(p.startsAt)) return false;
  if (p.endsAt && t > Date.parse(p.endsAt)) return false;
  if (p.days.length > 0 && !p.days.includes(ctx.dow)) return false;
  if (p.timeFrom && p.timeTo) {
    const from = toMinutes(p.timeFrom);
    const to = toMinutes(p.timeTo);
    const ok = from <= to ? ctx.minutes >= from && ctx.minutes <= to : ctx.minutes >= from || ctx.minutes <= to;
    if (!ok) return false;
  }
  return true;
}
function allocateProportional(amount, weights) {
  const total = weights.reduce((s, w) => s + w, 0);
  const out = weights.map(() => 0);
  if (total <= 0 || amount <= 0) return out;
  let used = 0;
  let last = -1;
  weights.forEach((w, i) => {
    if (w > 0) last = i;
  });
  weights.forEach((w, i) => {
    if (w <= 0) return;
    if (i === last) return;
    const share = Math.min(w, mulDivRound(amount, w, total));
    out[i] = share;
    used += share;
  });
  out[last] = Math.min(weights[last], amount - used);
  return out;
}
function evaluatePromos(lines, promos, ctx) {
  const gross = lines.map((l) => lineGross(l.unitPrice, l.qtyMilli));
  const remaining = lines.map((l, i) => Math.max(0, gross[i] - l.manualDiscount));
  const perLine = lines.map(() => 0);
  const touched = lines.map(() => false);
  const locked = lines.map(() => false);
  const applied = [];
  const ordered = promos.filter((p) => promoActiveFor(p, ctx)).sort((a, b) => b.priority - a.priority || a.id.localeCompare(b.id));
  for (const p of ordered) {
    const eligible = lines.map((l, i) => i).filter((i) => {
      const l = lines[i];
      if (remaining[i] <= 0 || locked[i]) return false;
      if (!p.stackable && touched[i]) return false;
      if (p.scope === "product") return p.targetIds.includes(l.productId);
      if (p.scope === "category")
        return l.categoryId != null && p.targetIds.includes(l.categoryId);
      return true;
    });
    if (eligible.length === 0) continue;
    const base = eligible.reduce((s, i) => s + remaining[i], 0);
    if (base < p.minSpend || base <= 0) continue;
    let want;
    if (p.kind === "bogo") {
      const group = p.buyQty + p.getQty;
      want = lines.map((l, i) => {
        if (!eligible.includes(i)) return 0;
        const units = Math.floor(l.qtyMilli / 1e3);
        const free = Math.floor(units / group) * p.getQty;
        return Math.min(remaining[i], free * l.unitPrice);
      });
    } else if (p.kind === "percent") {
      want = lines.map(
        (_, i) => eligible.includes(i) ? percentOfBp(remaining[i], p.valueBp) : 0
      );
    } else if (p.scope === "order") {
      want = allocateProportional(
        Math.min(p.valueAmount, base),
        lines.map((_, i) => eligible.includes(i) ? remaining[i] : 0)
      );
    } else {
      want = lines.map(
        (l, i) => eligible.includes(i) ? Math.min(remaining[i], mulDivRound(p.valueAmount, l.qtyMilli, 1e3)) : 0
      );
    }
    let total = want.reduce((s, v) => s + v, 0);
    if (total <= 0) continue;
    if (p.maxDiscount > 0 && total > p.maxDiscount) {
      want = allocateProportional(p.maxDiscount, want);
      total = want.reduce((s, v) => s + v, 0);
    }
    want.forEach((v, i) => {
      if (v <= 0) return;
      perLine[i] = perLine[i] + v;
      remaining[i] = remaining[i] - v;
      touched[i] = true;
      if (!p.stackable) locked[i] = true;
    });
    applied.push({ promoId: p.id, name: p.name, amount: total });
  }
  return { perLine, applied };
}
var loyaltyRuleSchema = z9.object({
  enabled: z9.boolean().default(true),
  /** Setiap kelipatan Rp `earnPerAmount` dari total transaksi memberi `pointsPerUnit` poin. */
  earnPerAmount: z9.number().int().min(100).max(1e8).default(1e4),
  pointsPerUnit: z9.number().int().min(1).max(1e3).default(1),
  /** Nilai rupiah satu poin saat ditukar. */
  redeemValue: z9.number().int().min(1).max(1e6).default(100),
  /** Minimum poin yang boleh ditukar sekali transaksi. */
  minRedeemPoints: z9.number().int().min(1).max(1e6).default(10)
});
var DEFAULT_LOYALTY = loyaltyRuleSchema.parse({});
function pointsEarned(rule, grandTotal) {
  if (!rule.enabled || grandTotal <= 0) return 0;
  return Math.floor(grandTotal / rule.earnPerAmount) * rule.pointsPerUnit;
}
function maxRedeemablePoints(rule, balance, payable) {
  if (!rule.enabled || balance <= 0 || payable <= 0) return 0;
  const byValue = Math.floor(payable / rule.redeemValue);
  const pts = Math.min(balance, byValue);
  return pts >= rule.minRedeemPoints ? pts : 0;
}
function applyPointsRedemption(rule, points, lineRemaining) {
  const amount = points * rule.redeemValue;
  const perLine = allocateProportional(amount, lineRemaining);
  return { perLine, amount: perLine.reduce((s, v) => s + v, 0) };
}
function computePromoPricing(lines, promos, ctx, redeem) {
  const r = evaluatePromos(lines, promos, ctx);
  const lineTotals = [...r.perLine];
  let pointsAmount = 0;
  let pointsUsed = 0;
  if (redeem && redeem.points > 0 && redeem.rule.enabled) {
    const remaining = lines.map(
      (l, i) => Math.max(0, lineGross(l.unitPrice, l.qtyMilli) - l.manualDiscount - r.perLine[i])
    );
    const value = redeem.points * redeem.rule.redeemValue;
    if (value <= remaining.reduce((s, v) => s + v, 0)) {
      const pts = applyPointsRedemption(redeem.rule, redeem.points, remaining);
      pts.perLine.forEach((v, i) => lineTotals[i] = lineTotals[i] + v);
      pointsAmount = pts.amount;
      pointsUsed = redeem.points;
    }
  }
  return { ...r, lineTotals, pointsAmount, pointsUsed };
}

// src/escpos.ts
var paperCols = (paper) => paper === 58 ? 32 : 48;
var MAP = {
  "\u201C": '"',
  "\u201D": '"',
  "\u201E": '"',
  "\u2018": "'",
  "\u2019": "'",
  "\u2013": "-",
  "\u2014": "-",
  "\u2026": "...",
  "\u2022": "*",
  "\xD7": "x",
  "\xB7": ".",
  "\u2192": "->",
  "\u2190": "<-",
  "\u2713": "v",
  "\xA0": " "
};
function toPrintable(s) {
  let out = "";
  for (const ch of s.normalize("NFD")) {
    const code = ch.codePointAt(0);
    if (code >= 768 && code <= 879) continue;
    if (MAP[ch] !== void 0) out += MAP[ch];
    else if (code >= 32 && code <= 126) out += ch;
    else if (code === 10 || code === 13 || code === 9) out += " ";
    else out += "?";
  }
  return out;
}
function wrapText(text2, width) {
  const out = [];
  for (const para of toPrintable(text2).split(/\r?\n/)) {
    let line = "";
    for (const word of para.split(/ +/).filter(Boolean)) {
      let w = word;
      while (w.length > width) {
        if (line) {
          out.push(line);
          line = "";
        }
        out.push(w.slice(0, width));
        w = w.slice(width);
      }
      if (!line) line = w;
      else if (line.length + 1 + w.length <= width) line += " " + w;
      else {
        out.push(line);
        line = w;
      }
    }
    out.push(line);
  }
  return out;
}
function twoColumn(left, right, width) {
  const r = toPrintable(right);
  const l = toPrintable(left);
  if (l.length + 1 + r.length <= width) return [l + " ".repeat(width - l.length - r.length) + r];
  const room = Math.max(1, width - r.length - 1);
  const parts = wrapText(l, room);
  const last = parts.pop() ?? "";
  const rows = parts.slice();
  rows.push(last + " ".repeat(Math.max(1, width - last.length - r.length)) + r);
  return rows;
}
var ESC = 27;
var GS = 29;
var LF = 10;
var DRAWER_PULSE = [ESC, 112, 0, 25, 250];
function renderEscPos(doc, opts) {
  const out = [ESC, 64];
  const text2 = (s) => {
    for (const ch of s) out.push(ch.charCodeAt(0) & 127);
  };
  const align = (a) => out.push(ESC, 97, a === "left" ? 0 : a === "center" ? 1 : 2);
  const bold = (on) => out.push(ESC, 69, on ? 1 : 0);
  const size = (s) => out.push(GS, 33, s === 1 ? 0 : s === 2 ? 1 : 17);
  if (doc.drawer) out.push(...DRAWER_PULSE);
  for (const l of doc.lines) {
    if (l.t === "text") {
      const s = l.size ?? 1;
      const width = s === 3 ? Math.floor(opts.cols / 2) : opts.cols;
      align(l.align ?? "left");
      bold(!!l.bold);
      size(s);
      for (const row of wrapText(l.text, width)) {
        text2(row);
        out.push(LF);
      }
      size(1);
      bold(false);
      align("left");
    } else if (l.t === "row") {
      bold(!!l.bold);
      for (const row of twoColumn(l.left, l.right, opts.cols)) {
        text2(row);
        out.push(LF);
      }
      bold(false);
    } else if (l.t === "rule") {
      text2((l.ch ?? "-").repeat(opts.cols));
      out.push(LF);
    } else {
      for (let i = 0; i < Math.max(0, Math.min(l.n, 20)); i++) out.push(LF);
    }
  }
  if (doc.cut !== false) out.push(ESC, 100, 4, GS, 86, 66, 0);
  return Uint8Array.from(out);
}
var rp = (n) => `Rp ${new Intl.NumberFormat("id-ID").format(n)}`;
var neg = (n) => `-${rp(n)}`;
function stamp(iso, timezone = "Asia/Jakarta") {
  let tz = timezone;
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone: tz });
  } catch {
    tz = "Asia/Jakarta";
  }
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).format(new Date(iso)).replace(",", "");
}
var hr = { t: "rule" };
var centered = (text2, bold = false, size = 1) => ({
  t: "text",
  text: text2,
  align: "center",
  bold,
  size
});
function receiptDoc(o, b, opts = {}) {
  const L = [];
  L.push(centered(b.name || b.outletName, true, 2));
  if (b.name && b.name !== b.outletName) L.push(centered(b.outletName));
  if (b.npwp) L.push(centered(`NPWP ${b.npwp}`));
  if (b.header) L.push(centered(b.header));
  L.push(hr);
  L.push({ t: "row", left: "No", right: o.orderNo });
  L.push({ t: "row", left: "Waktu", right: stamp(o.closedAt, b.timezone) });
  if (o.employeeName) L.push({ t: "row", left: "Kasir", right: o.employeeName });
  if (o.tableName) L.push({ t: "row", left: "Meja", right: o.tableName });
  if (o.customerName) L.push({ t: "row", left: "Pelanggan", right: o.customerName });
  L.push(hr);
  for (const i of o.items) {
    L.push({ t: "text", text: i.name + (i.variantName ? ` (${i.variantName})` : "") });
    if (i.modifiers.length)
      L.push({ t: "text", text: "  + " + i.modifiers.map((m) => m.name).join(", ") });
    if (i.note) L.push({ t: "text", text: `  "${i.note}"` });
    const unit = i.unitPrice + i.modifiersTotal;
    L.push({
      t: "row",
      left: `${i.qty} x ${rp(unit)}`,
      right: rp(Math.round(i.qty * unit))
    });
    if (i.discount > 0) L.push({ t: "row", left: "  Diskon", right: neg(i.discount) });
  }
  L.push(hr);
  const promoSum = (o.promos ?? []).reduce((s, p) => s + p.amount, 0);
  L.push({ t: "row", left: "Subtotal", right: rp(o.subtotal + promoSum) });
  for (const p of o.promos ?? [])
    L.push({ t: "row", left: `Promo ${p.name}`, right: neg(p.amount) });
  if (o.voucherCode) L.push({ t: "text", text: `  Voucher ${o.voucherCode}` });
  if (o.discount && o.discount.amount > 0)
    L.push({ t: "row", left: "Diskon", right: neg(o.discount.amount) });
  if (o.service > 0)
    L.push({ t: "row", left: `Service ${b.serviceBp / 100}%`, right: rp(o.service) });
  if (o.tax > 0)
    L.push({
      t: "row",
      left: `PPN ${b.taxBp / 100}%${b.taxInclusive ? " (incl)" : ""}`,
      right: rp(o.tax)
    });
  if (o.rounding !== 0) L.push({ t: "row", left: "Pembulatan", right: rp(o.rounding) });
  L.push({ t: "row", left: "TOTAL", right: rp(o.grandTotal), bold: true });
  const label = opts.payLabel ?? ((m) => m === "cash" ? "Tunai" : m.toUpperCase());
  for (const p of o.payments) {
    if (p.method === "cash") {
      L.push({ t: "row", left: label(p.method), right: rp(p.tendered) });
      L.push({ t: "row", left: "Kembali", right: rp(p.change) });
    } else L.push({ t: "row", left: label(p.method), right: rp(p.amount) });
  }
  if (o.pointsRedeemed) L.push({ t: "row", left: "Poin ditukar", right: String(o.pointsRedeemed) });
  if (o.pointsEarned) L.push({ t: "row", left: "Poin diperoleh", right: `+${o.pointsEarned}` });
  if (o.status === "void") L.push(centered("*** DIBATALKAN ***", true));
  if (b.footer) {
    L.push({ t: "feed", n: 1 });
    L.push(centered(b.footer));
  }
  return { lines: L, cut: true, drawer: opts.drawer };
}
function shiftDoc(shift, s, outletName, timezone) {
  const c = s.cash;
  const L = [
    centered("LAPORAN SHIFT", true, 2),
    centered(outletName),
    hr,
    { t: "row", left: "Dibuka", right: stamp(shift.openedAt, timezone) }
  ];
  if (shift.closedAt) L.push({ t: "row", left: "Ditutup", right: stamp(shift.closedAt, timezone) });
  if (shift.openedByName) L.push({ t: "row", left: "Kasir", right: shift.openedByName });
  L.push(
    hr,
    { t: "row", left: "Transaksi", right: String(s.orders) },
    { t: "row", left: "Dibatalkan", right: String(s.voided) },
    { t: "row", left: "Penjualan kotor", right: rp(s.gross) },
    { t: "row", left: "Diskon", right: neg(s.discounts) },
    { t: "row", left: "Service", right: rp(s.service) },
    { t: "row", left: "PPN", right: rp(s.tax) },
    { t: "row", left: "Pembulatan", right: rp(s.rounding) },
    { t: "row", left: "PENJUALAN BERSIH", right: rp(s.netSales), bold: true },
    hr,
    { t: "row", left: "Modal awal", right: rp(c.opening) },
    { t: "row", left: "Penjualan tunai", right: rp(c.sales) },
    { t: "row", left: "Kas masuk", right: rp(c.cashIn) },
    { t: "row", left: "Kas keluar", right: neg(c.cashOut) },
    { t: "row", left: "Kas diharapkan", right: rp(c.expected), bold: true }
  );
  if (c.counted != null) L.push({ t: "row", left: "Kas dihitung", right: rp(c.counted) });
  if (c.variance != null) L.push({ t: "row", left: "Selisih", right: rp(c.variance), bold: true });
  return { lines: L, cut: true };
}
function ticketDoc(t, timezone) {
  const L = [
    centered(t.stationName ?? "DAPUR", true, 3),
    centered(t.label, true, 2),
    { t: "row", left: `Putaran ${t.roundNo}`, right: stamp(t.createdAt, timezone) },
    hr
  ];
  for (const i of t.items.filter((x) => x.status !== "cancelled")) {
    L.push({ t: "text", text: `${i.qty}x ${i.name}`, bold: true, size: 2 });
    if (i.modifiers.length) L.push({ t: "text", text: "  + " + i.modifiers.join(", ") });
    if (i.note) L.push({ t: "text", text: `  "${i.note}"` });
  }
  return { lines: L, cut: true };
}
function billCheckDoc(bill, b) {
  const L = [
    centered(b.name || b.outletName, true, 2),
    centered("TAGIHAN SEMENTARA", true),
    { t: "row", left: "Meja/Bill", right: bill.label },
    hr
  ];
  for (const l of bill.lines) {
    L.push({ t: "text", text: l.name });
    L.push({ t: "row", left: `${l.qty} x ${rp(l.unitPrice)}`, right: rp(l.net) });
  }
  L.push(hr, { t: "row", left: "Subtotal", right: rp(bill.subtotal) });
  if (bill.service > 0)
    L.push({ t: "row", left: `Service ${b.serviceBp / 100}%`, right: rp(bill.service) });
  if (bill.tax > 0) L.push({ t: "row", left: `PPN ${b.taxBp / 100}%`, right: rp(bill.tax) });
  if (bill.rounding !== 0) L.push({ t: "row", left: "Pembulatan", right: rp(bill.rounding) });
  L.push({ t: "row", left: "TOTAL", right: rp(bill.grandTotal), bold: true });
  L.push(centered("Belum dibayar"));
  return { lines: L, cut: true };
}
function testDoc(cols, opts = {}) {
  const ruler = Array.from({ length: cols }, (_, i) => String((i + 1) % 10)).join("");
  return {
    lines: [
      centered("UJI PRINTER", true, 3),
      { t: "text", text: ruler },
      { t: "rule", ch: "=" },
      { t: "text", text: "Teks normal rata kiri" },
      { t: "text", text: "Teks tebal", bold: true },
      { t: "text", text: "Tinggi ganda", size: 2 },
      { t: "text", text: "Rata tengah", align: "center" },
      { t: "text", text: "Rata kanan", align: "right" },
      { t: "row", left: "2 x Rp 25.000", right: "Rp 50.000" },
      {
        t: "row",
        left: "Nama produk yang sangat panjang sekali sampai membungkus",
        right: "Rp 9.999"
      },
      { t: "text", text: "Aksen & tanda: caf\xE9 \u201Ckutip\u201D \u2013 strip" },
      { t: "rule" },
      centered(`${cols} karakter per baris`)
    ],
    cut: true,
    drawer: opts.drawer
  };
}

// src/sync-proof.ts
function canonicalJson(v) {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v))
    return `[${v.map((x) => canonicalJson(x === void 0 ? null : x)).join(",")}]`;
  const o = v;
  return `{${Object.keys(o).filter((k) => o[k] !== void 0).sort().map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(",")}}`;
}
var syncProofMessage = (i) => `sync1
${i.id}
${i.seq}
${i.type}
${i.employeeId}
${i.createdAt}
${canonicalJson(i.payload)}`;
var approvalProofMessage = (orderId, approverId, at) => `approve1
${orderId}
${approverId}
${at}`;
var b64uToBytes = (s) => {
  const b = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(new ArrayBuffer(b.length));
  for (let i = 0; i < b.length; i++) out[i] = b.charCodeAt(i);
  return out;
};
var bytesToB64u = (b) => {
  let s = "";
  for (const x of new Uint8Array(b)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};
async function hmacB64u(keyB64u, message) {
  const key = await crypto.subtle.importKey(
    "raw",
    b64uToBytes(keyB64u),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return bytesToB64u(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(message)));
}
function safeEqual(a, b) {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return d === 0;
}
export {
  ANNOUNCEMENT_LEVELS,
  BILL_TYPES,
  DEFAULT_LOYALTY,
  DRAWER_PULSE,
  FEATURES,
  FEATURE_GROUPS,
  FEATURE_KEYS,
  FEATURE_PARENT,
  IMPERSONATION_TTL_SEC,
  LARGE_DISCOUNT_PERCENT,
  LEGACY_FEATURE_KEYS,
  MAX_QTY_MILLI,
  MAX_SUBTOTAL,
  MAX_SYNC_BATCH,
  MAX_UNIT_PRICE,
  MOVEMENT_LABELS,
  NON_CASH_METHODS,
  ORDER_FLAGS,
  ORDER_TYPES,
  PAYMENT_ENVIRONMENTS,
  PAYMENT_LABELS,
  PAYMENT_STATUSES,
  PAYMENT_TTL_MINUTES,
  PERMISSIONS,
  PRODUCT_TYPES,
  PROMO_KINDS,
  PROMO_SCOPES,
  ROUNDING_RULES,
  SENSITIVE_POS_PERMISSIONS,
  STOCK_MOVEMENT_TYPES,
  SYNC_TYPES,
  SYSTEM_ROLES,
  TICKET_STATUSES,
  activateDeviceSchema,
  activationCodeInputSchema,
  allocateProportional,
  announcementInputSchema,
  applyPointsRedemption,
  approvalProofMessage,
  areaInputSchema,
  assertRupiah,
  billCancelSchema,
  billCheckDoc,
  billMergeSchema,
  billMoveSchema,
  billPayRequestSchema,
  billSendSchema,
  billSplitSchema,
  billUpsertSchema,
  billVoidItemSchema,
  buildOrderNo,
  bundleItemInputSchema,
  businessTypeSchema,
  calculateOrder,
  canTransitionTicket,
  canonicalJson,
  cashChange,
  cashMovementSchema,
  cashPaymentSchema,
  categoryInputSchema,
  categoryUpdateSchema,
  compareVersions,
  computeOrder,
  computePromoPricing,
  computeReturnRefund,
  deviceModeSchema,
  divRound,
  emailLoginSchema,
  employeeInputSchema,
  employeeUpdateSchema,
  evaluatePromos,
  forgotPasswordSchema,
  formatRupiah,
  hasAllPermissions,
  hasPermission,
  hashPin,
  hmacB64u,
  impersonateSchema,
  isFeatureKey,
  isKnownPermission,
  isLargeDiscount,
  isValidPin,
  itemUnitPrice,
  lineGross,
  localClock,
  localParts,
  loginSchema,
  loyaltyRuleSchema,
  mapMidtransStatus,
  maxRedeemablePoints,
  modifierGroupInputSchema,
  modifierInputSchema,
  movingAverageCost,
  mulDivRound,
  nonCashPaymentSchema,
  offlineApprovalSchema,
  opnameCountSchema,
  opnameCreateSchema,
  orderCalcLines,
  orderDiscountInputSchema,
  orderNoSchema,
  orderTotalsSchema,
  orderVoidPayloadSchema,
  outletInputSchema,
  outletModeSchema,
  outletProductInputSchema,
  outletSettingSchema,
  outletUpdateSchema,
  paperCols,
  paymentConfigSchema,
  paymentIntentCreateSchema,
  percentOfBp,
  pinLoginSchema,
  planInputSchema,
  platformChangePasswordSchema,
  platformLoginSchema,
  poCreateSchema,
  poReceiveSchema,
  poUpdateSchema,
  pointsEarned,
  posAuthorizeSchema,
  posOrderItemSchema,
  posOrderSchema,
  posPinLoginSchema,
  pricingSchema,
  productInputSchema,
  productUpdateSchema,
  promoActiveFor,
  promoBodySchema,
  receiptDoc,
  recipeSchema,
  registerSchema,
  renderEscPos,
  resetPasswordSchema,
  resetPinSchema,
  resolveFeatures,
  returnCreateSchema,
  roleInputSchema,
  roleUpdateSchema,
  roundHalfUp,
  rupiahSchema,
  safeEqual,
  shiftClosePayloadSchema,
  shiftCloseSchema,
  shiftDoc,
  shiftOpenSchema,
  soldOutSchema,
  stationInputSchema,
  stockAdjustSchema,
  stockMinSchema,
  stockRepackSchema,
  supplierInputSchema,
  syncEnvelopeSchema,
  syncPayloadSchemas,
  syncProofMessage,
  tableBulkSchema,
  tableInputSchema,
  tenantFeatureSchema,
  tenantPlanSchema,
  testDoc,
  ticketDoc,
  ticketItemPatchSchema,
  ticketPatchSchema,
  toOrderDiscount,
  toPrintable,
  toQtyMilli,
  totalsOf,
  totpCodeSchema,
  transferCreateSchema,
  transferReceiveSchema,
  twoColumn,
  uuidSchema,
  uuidv7,
  variantInputSchema,
  verifyPin,
  voidOrderSchema,
  wrapText
};
